import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import axios from 'axios';
import { formatMpRecommendation } from '@/components/mpRecommendation/mpRecommendationHelper';
import { computeSelectionHash, formatBetBuilderRecommendations } from '@/core/lib/betBuilderRecommendationHelper';
import type { IBetBuilderCombinationSelectionDisplayRule } from '@/interface/betBuilder';
import { RecommendType, SportType } from '@/interface/enum';
import type { RogueModelsRequestsBetBuilderPreBuiltForMatchRequest, RogueModelsRequestsPopularMixParlayRequest, RogueModelsResponsesBetBuilderPreBuiltResponse, RogueModelsResponsesPopularMixParlayResponse } from '@/interface/rogue';
import { useToggleStore } from '@/store/toggleStore';

function createInstance(baseUrl: string) {
    const instance = axios.create({
        baseURL: baseUrl,
        timeout: 15000,
        withCredentials: true,
    });

    return instance;
}

export function getApiInstance() {
    const { recommendationApiUrl } = useToggleStore();
    return createInstance(recommendationApiUrl);
}

const notDefinedType: unique symbol = Symbol('nonExist');
type NotDefinedType = typeof notDefinedType

type TypedParamsAxiosRequestConfig<TParams> = Omit<AxiosRequestConfig, 'params'> & { params?: TParams}

/** wrapper for `axios.get` with request & response type checking */
function get<TRequest = NotDefinedType, TResponse = NotDefinedType>(
    url: string,
    config?: TypedParamsAxiosRequestConfig<TRequest extends NotDefinedType ? 'You must provide type parameters' : NoInfer<TRequest>>,
): Promise<AxiosResponse<TResponse>> {
    return getApiInstance().get<TResponse>(url, config);
}

export const RecommendationApi = {
    async getMpRecommendations(sportType: SportType, recommendType: RecommendType, count: number) {
        const response = await get<RogueModelsRequestsPopularMixParlayRequest, RogueModelsResponsesPopularMixParlayResponse>('/popularMixParlay', {
            params: {
                sportType: SportType.toRogue(sportType),
                recommendType: RecommendType.toRogue(recommendType),
                count,
            },
        });
        return formatMpRecommendation(response.data);
    },

    async getBetBuilderRecommendationsByMatch(matchId: number, displayRule: IBetBuilderCombinationSelectionDisplayRule) {
        const selectionHash = computeSelectionHash(displayRule);
        const response = await get<RogueModelsRequestsBetBuilderPreBuiltForMatchRequest, RogueModelsResponsesBetBuilderPreBuiltResponse>(`/betBuilderPreBuilt/${matchId}/${selectionHash}`);
        return formatBetBuilderRecommendations(response.data);
    },
};
